import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ChangeEvent, DragEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildAttachmentDownloadUrl, buildDraftAttachmentDownloadUrl, useAttachments } from "./useAttachments";

type AttachmentPanelProps = {
    tableName: string;
    recordId?: string | null;
    draftToken?: string | null;
    columnName?: string | null;
    title?: string;
    compact?: boolean;
    disabled?: boolean;
    variant?: "panel" | "field";
    collapsible?: boolean;
};

const formatBytes = (value: number) => {
    if (!value) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
    return `${(value / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

export const AttachmentPanel = ({
    tableName,
    recordId,
    draftToken,
    columnName,
    title,
    compact = false,
    disabled = false,
    variant = "panel",
    collapsible = false,
}: AttachmentPanelProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [expanded, setExpanded] = useState(!collapsible);
    const hasInitializedExpansion = useRef(false);
    const attachments = useAttachments(tableName, recordId, columnName, draftToken);
    const useDraft = Boolean(!recordId && draftToken);

    const isUnavailable = disabled || !attachments.canUse;
    const isBusy = attachments.upload.isPending || attachments.remove.isPending;
    const error = attachments.error || attachments.upload.error || attachments.remove.error;

    useEffect(() => {
        if (!collapsible || attachments.isLoading || hasInitializedExpansion.current) {
            return;
        }

        setExpanded(attachments.attachments.length > 0);
        hasInitializedExpansion.current = true;
    }, [attachments.attachments.length, attachments.isLoading, collapsible]);

    const uploadFiles = (files: FileList | File[]) => {
        Array.from(files).forEach((file) => attachments.upload.mutate(file));
    };

    const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            uploadFiles(event.target.files);
        }
        event.target.value = "";
    };

    const onDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        if (!isUnavailable && event.dataTransfer.files.length) {
            uploadFiles(event.dataTransfer.files);
        }
    };

    const downloadUrl = (attachmentId: string) => useDraft
        ? buildDraftAttachmentDownloadUrl(tableName, draftToken!, attachmentId, columnName)
        : buildAttachmentDownloadUrl(tableName, recordId!, attachmentId, columnName);

    if (variant === "field") {
        return (
            <Box>
                <Box
                    sx={{
                        minHeight: 42,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1,
                        py: 0.75,
                        border: "1px solid",
                        borderColor: isDragging ? "primary.main" : "rgba(0, 0, 0, 0.23)",
                        borderRadius: 1,
                        bgcolor: isDragging ? "action.hover" : disabled ? "action.disabledBackground" : "background.paper",
                        transition: "border-color 120ms ease, background-color 120ms ease",
                        "&:hover": {
                            borderColor: disabled ? "rgba(0, 0, 0, 0.23)" : "text.primary",
                        },
                    }}
                    onDragOver={(event) => {
                        event.preventDefault();
                        if (!isUnavailable) setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={onDrop}
                >
                    <AttachFileIcon fontSize="small" color="action" />

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {attachments.isLoading ? (
                            <CircularProgress size={18} />
                        ) : attachments.attachments.length > 0 ? (
                            <Stack direction="row" gap={0.75} flexWrap="wrap">
                                {attachments.attachments.map((attachment) => (
                                    <Chip
                                        key={attachment.id}
                                        size="small"
                                        variant="outlined"
                                        icon={<AttachFileIcon />}
                                        label={attachment.filename}
                                        component="a"
                                        href={downloadUrl(attachment.id)}
                                        clickable
                                        onDelete={isUnavailable || isBusy ? undefined : () => attachments.remove.mutate(attachment.id)}
                                        deleteIcon={<DeleteOutlineIcon />}
                                        sx={{
                                            maxWidth: "100%",
                                            "& .MuiChip-label": {
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            },
                                        }}
                                    />
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary" noWrap>
                                {attachments.canUse
                                    ? t("No files selected")
                                    : t("Save the record before adding attachments.")}
                            </Typography>
                        )}
                    </Box>

                    <Tooltip title={String(t("Upload"))}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={isUnavailable || isBusy}
                                onClick={() => inputRef.current?.click()}
                            >
                                <UploadFileIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <input ref={inputRef} type="file" multiple hidden onChange={onFileChange} />
                </Box>

                {attachments.upload.isPending && <LinearProgress sx={{ mt: 0.75 }} />}

                {error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {(error as any)?.response?.data?.error?.message || (error as Error).message}
                    </Alert>
                )}
            </Box>
        );
    }

    const panelContent = (
        <Paper
            variant="outlined"
            sx={{
                p: compact ? 1.5 : 2,
                borderStyle: isDragging ? "solid" : "dashed",
                borderColor: isDragging ? "primary.main" : "divider",
                borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                bgcolor: isDragging ? "action.hover" : "background.paper",
            }}
            onDragOver={(event) => {
                event.preventDefault();
                if (!isUnavailable) setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
        >
            <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <AttachFileIcon fontSize="small" />
                        <Typography variant={compact ? "body2" : "subtitle2"}>
                            {title || t("Attachments")}
                        </Typography>
                    </Stack>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        disabled={isUnavailable || isBusy}
                        onClick={() => inputRef.current?.click()}
                        sx={{ minHeight: { xs: 40, sm: 30 } }}
                    >
                        {t("Upload")}
                    </Button>
                </Stack>

                <input ref={inputRef} type="file" multiple hidden onChange={onFileChange} />

                {!attachments.canUse && (
                    <Alert severity="info">{t("Save the record before adding attachments.")}</Alert>
                )}

                {error && (
                    <Alert severity="error">
                        {(error as any)?.response?.data?.error?.message || (error as Error).message}
                    </Alert>
                )}

                {attachments.isLoading && <CircularProgress size={20} />}
                {attachments.upload.isPending && <LinearProgress />}

                <List dense disablePadding>
                    {attachments.attachments.map((attachment) => (
                        <ListItem
                            key={attachment.id}
                            disableGutters
                            secondaryAction={
                                <Stack direction="row">
                                    <Tooltip title={String(t("Download"))}>
                                        <IconButton
                                            edge="end"
                                            component="a"
                                            href={downloadUrl(attachment.id)}
                                            size="small"
                                            sx={{ width: { xs: 40, sm: 32 }, height: { xs: 40, sm: 32 } }}
                                        >
                                            <DownloadIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={String(t("Delete"))}>
                                        <IconButton
                                            edge="end"
                                            size="small"
                                            disabled={isUnavailable || isBusy}
                                            onClick={() => attachments.remove.mutate(attachment.id)}
                                            sx={{ width: { xs: 40, sm: 32 }, height: { xs: 40, sm: 32 } }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            }
                        >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <AttachFileIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={attachment.filename}
                                secondary={`${attachment.content_type || t("Unknown type")} - ${formatBytes(attachment.byte_size)}`}
                                primaryTypographyProps={{ noWrap: true }}
                                secondaryTypographyProps={{ noWrap: true }}
                            />
                        </ListItem>
                    ))}
                </List>

                {!attachments.isLoading && attachments.attachments.length === 0 && attachments.canUse && (
                    <Box sx={{ color: "text.secondary", fontSize: 13 }}>
                        {isMobile ? t("Use the upload button to add files.") : t("Drop files here or use the upload button.")}
                    </Box>
                )}
            </Stack>
        </Paper>
    );

    if (collapsible) {
        const count = attachments.attachments.length;

        return (
            <Accordion
                expanded={expanded}
                onChange={(_event, nextExpanded) => setExpanded(nextExpanded)}
                disableGutters
                elevation={1}
                sx={{
                    borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                    boxShadow: (theme) => theme.shadows[1],
                    overflow: "hidden",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": {
                        margin: 0,
                    },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                        <AttachFileIcon fontSize="small" />
                        <Typography variant="subtitle2" noWrap>
                            {title || t("Attachments")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {count > 0 ? `(${count})` : `(${t("Empty")})`}
                        </Typography>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    {panelContent}
                </AccordionDetails>
            </Accordion>
        );
    }

    return panelContent;
};
